// Phase 5b — report-generation round-trip kit.
//
// One data-driven module reproducing the legacy Generate chain end to end:
//
//   main_header.js generateReport (:501-556)  → per-report dispatch state(s)
//   controller/action.js generateReport (:209-224) → submittal 1h guard,
//     empty-board/tab swap, updateReportsData
//   controller/action.js updateReportsData (:112-181) → optimistic
//     'pending_reports' rows + same-tab storage event + the 4.5s/5s UI timers
//   tabs/<report>/view/*.js generateReport → the actual POST (URL + payload
//     per report; see REPORT_DISPATCH below, each entry cites its source)
//
// The kit is UI-free: the page supplies GenerateUi callbacks for the DOM
// effects the legacy controller does with jQuery (preloaders, popup, tab
// swap). 5c–5e per-report tabs call the same generateReport() and only add
// their own render-on-result views.

import {
  ajaxPostToCelery,
  postToCelery,
  rejectionMessage,
  waitForCeleryTask,
  type FormValue,
} from 'app/celeryRoundTrip';
import {
  fetchCollection,
  generateStateKey,
  reportCacheKey,
  storeGeneratedReport,
} from './reportTabData';
import {
  captureFanCanvases,
  createFanCanvases,
  drawFanDataCurves,
  prepareFanRows,
  type FanCanvasEntry,
  type FanDataRow,
} from './fanCurves';
import {
  LAST_GENERATED_REPORT_KEY,
  SHOW_REPORTS_LIST_EVENT,
  getAvailableFormatsForReport,
  getUserInitials,
  readPendingReports,
  removePendingReportsWhere,
  writePendingReportsAndNotify,
  type PendingReportRow,
} from 'app/pendingReports';
import { getCookie, getKsdmConfig } from 'helpers/index';
import { track } from 'app/analytics';
import type { IReportAhuRow, IReportType } from 'app/services/reportsBoardApi';
import { getReportType, hasScope } from './reportScopes';

// ---- Dispatch state (the legacy main_header `self.state` at Generate time) ----

/**
 * Key order mirrors the legacy object's insertion order (render :224-230 →
 * reportIsGenerated :244 → generateReport :504-510,554-555) so form bodies
 * serialize identically.
 */
export interface GenerateState {
  project_id: string; // '0' when none (action.js:211)
  ahu_id: string; // '0' when none (action.js:212)
  selected_ahu_ids: number[]; // render-time gating list (main_header.js:227)
  current_report: string; // report VALUE
  reportType: string | null; // getReportType (main_header.js:1041-1048)
  generated: boolean; // reportIsGenerated stamps false pre-generate (action.js:244)
  current_report_name: string; // display name ($current.data('name'), :505)
  project_name: string; // selected_project_name() or '' (:506-508)
  ahu_ids: number[]; // multi-select snapshot (:554)
  ahu_ids_json: string; // JSON.stringify(picked) (:555)
  /**
   * ⚠ dev-sync G / CB-2 — VERIFIED BY INSPECTION ONLY, NEVER NON-ZERO.
   * main_header.js:582-593 — how many FMI drawing pages the picked AHU will
   * produce (`reports.fmi_assembly_page_count`), 0 for every other report and
   * for a multi-AHU pick. Read ONLY by recordPendingReports, to force the
   * pending row's format to 'zip' when >1. Deliberately NOT in wholeState():
   * the legacy carries it into the POST body only because jQuery serializes
   * `self.state` wholesale, and no backend anywhere reads it
   * (`grep -rn fmi_page_count django/` → 6 hits, all client JS). Always 0 on
   * dev3: there is no `ksdm_master_ahu_report` row for value='fmi_assembly'.
   */
  fmi_page_count: number;
  /**
   * Dev-sync I — the Coil Analytics reporting period (main_header.js:699-704
   * on merged dev: `if ($current.val() === 'coil_analytics') {
   * self.state.start_date = …; self.state.end_date = …; }`). Stamped ONLY for
   * coil_analytics dispatches; ISO 'YYYY-MM-DD'.
   */
  start_date?: string;
  end_date?: string;
}

export interface GenerateInput {
  report: IReportType;
  projectId: string | null;
  ahuId: string | null;
  /** Button-caption project name ('All' fallback) — selected_project_name()
   *  (main_header.js:1004-1016). */
  projectName: string;
  /** Multi-select snapshot, ENABLED rows only — the legacy re-reads the DOM
   *  with `:checked:not(:disabled)` (main_header.js:516-519). */
  pickedAhuIds: number[];
  /** Render-time selected_ahu_ids (may include disabled section rows). */
  selectedAhuIds: number[];
  /**
   * ⚠ dev-sync G / CB-2 — the header's rendered AHU rows (`self.data.ahus`,
   * main_header.js:583-585), read ONLY to look up the picked AHU's
   * `reports.fmi_assembly_page_count`. Optional: every caller that is not
   * generating FMI can omit it.
   */
  ahuRows?: IReportAhuRow[];
  /** Dev-sync I — the header's Coil Analytics Start/End dates (the page owns
   *  them, like the legacy view's coilAnalyticsStartDate/EndDate fields). Read
   *  only when generating coil_analytics. */
  coilAnalyticsRange?: { start: string; end: string };
}

/** DOM effects the legacy controller performs; supplied by the page. */
export interface GenerateUi {
  /** Submittal 1h guard tripped: tabs preloader off + info popup
   *  (action.js:213-218 + view/main.js:57-59). */
  onBlocked: () => void;
  /** The server refused this dispatch and said why (e.g. AHU Acoustics with
   *  no selected-and-rated fan, reachable when the board's AHU list has gone
   *  stale). The optimistic pending row has already been taken back; this is
   *  for telling the user. Absent for a network failure or an HTML error
   *  page, which are not messages to show. */
  onRejected?: (message: string) => void;
  /** Per dispatch: `$('#empty_board').removeClass('active')` +
   *  `$('#<report>').show()` (action.js:219,223). */
  onDispatched: (state: GenerateState) => void;
  /** 4.5s: generation-in-progress pane fades (`opacity-0`) + tabs preloader
   *  off (action.js:124-128). */
  onSettling: () => void;
  /** 5s: report pane hides, empty board re-activates (action.js:176-180). */
  onFinished: () => void;
  /**
   * 5c: the dispatch POST resolved — the legacy tab's `success: function() {
   * self.render(); }` moment (e.g. weight_calc.js:114-116, _2d_drawing.js:64-66):
   * the tab fills with its shell and `$('.report-tabs-preloader')` deactivates
   * (each tab's render(), e.g. weight_calc.js:40). NOT called when the POST
   * fails — the legacy error path leaves the pane empty. Fires once per
   * dispatch state (N times on a multi-AHU fan-out).
   */
  onTabRendered?: (state: GenerateState) => void;
}

// ---- Legacy report-scope sets ----

/** AHU-scope reports that send ONE batched request (backend ZIP) instead of
 *  the N-task split (main_header.js:531-539). */
export const BATCHED_MULTI_AHU_REPORTS = [
  'fan_data',
  'coil_data',
  'gas_heater_data',
  'evap_pack_data',
  'weight_calc',
  'center_of_gravity_calc',
  'ahu_acoustics',
  'bill_of_materials',
  'mfs_profile_cut_list',
  // ⚠ dev-sync G / CB-2 — VERIFIED BY INSPECTION ONLY, NEVER EXECUTED.
  // main_header.js:566-567 adds `!isFmiAssembly` to the fan-out guard, so a
  // multi-AHU FMI pick sends ONE POST to /reports/fmi_assembly/ahu (the view
  // turns ≥2 ahu_ids into a single zip task) instead of N single-AHU POSTs.
  // Being in this list also puts it in MULTI_AHU_ZIP_REPORTS below, which is
  // action.js:145's `currentReport === 'fmi_assembly'` clause. UNTESTABLE on
  // dev3: no `ksdm_master_ahu_report` row for value='fmi_assembly'.
  'fmi_assembly',
];

/** Multi-AHU dispatches whose pending row is a single forced 'zip'
 *  (action.js:134-145). */
const MULTI_AHU_ZIP_REPORTS = new Set([
  'unit_technical_data',
  'pressure_drop_calc',
  'electrical_calc',
  ...BATCHED_MULTI_AHU_REPORTS,
]);

/** No pending rows for these (action.js:115-118) — and, because legacy
 *  returns before the timers, no 4.5s/5s UI reverts either. Re-verified for
 *  5e: the return sits AFTER the 'generate:report:<name>' trigger (:114) but
 *  BEFORE the pending-rows read (:119), timer2/timer3 (:124-133) and the
 *  unnamed 5s hide timer (:176-180) — so a service/shipping generate keeps its
 *  pane up indefinitely (the socket result renders in place and stays), fires
 *  no SHOW_REPORTS_LIST_EVENT and writes no pending row. sales_order is NOT in
 *  the set: it gets rows + all timers (its in-tab render is hidden by the 5s
 *  revert and comes back through the reopen path). */
const NO_PENDING_ROW_REPORTS = new Set(['service_order', 'shipping_order']);

/** The 1h duplicate guard applies to these values (action.js:96). */
const SUBMITTAL_GUARDED_REPORTS = new Set(['submittal_package']);

const ONE_HOUR_MS = 60 * 60 * 1000;
const SETTLE_MS = 4500; // action.js:124-128 (timer2)
const FINISH_MS = 5000; // action.js:130-133 (timer3) + :176-180 (timer)

// ---- Public API ----

/**
 * The Generate entry point for BOTH the header ⟳ button and the empty-board
 * icon (legacy: both funnel into main_header.generateReport via the
 * 'generate_report_icon:clicked' dispatch, main_header.js:74-78).
 */
export function generateReport(input: GenerateInput, ui: GenerateUi): void {
  const states = buildGenerateStates(input);
  states.forEach((state) => dispatchGenerate(state, ui));
}

/**
 * main_header.js:501-556 — one state per dispatch. Most AHU-scope reports
 * with >1 picked AHUs fan out into N single-AHU states; the batched
 * allow-list (and every non-AHU report) dispatches once with the full pick.
 */
export function buildGenerateStates(input: GenerateInput): GenerateState[] {
  const { report, pickedAhuIds } = input;
  const base = (): GenerateState => ({
    project_id: input.projectId ?? '',
    ahu_id: input.ahuId ?? '',
    selected_ahu_ids: input.selectedAhuIds,
    current_report: report.value,
    // getReportType (main_header.js:1041-1048) — plain hasClass('ahu') wins
    // first, so project-type reports (classes "project ahu force-ahu")
    // report 'ahu'; reportScopes.getReportType is the same table.
    reportType: getReportType(report),
    generated: false,
    current_report_name: report.name,
    project_name: input.projectName,
    ahu_ids: pickedAhuIds,
    ahu_ids_json: JSON.stringify(pickedAhuIds),
    // main_header.js:592 — the `else` arm: 0 for every report that is not a
    // single-AHU FMI pick. The fan-out branch below returns before :582, so
    // its states keep this 0 too.
    fmi_page_count: 0,
  });

  // isAhuReport (:530): .ahu without .force-ahu and without .project — i.e.
  // the true AHU-scope reports; Unit Technical Data / Pressure Drop /
  // Electrical Calc carry force-ahu+project and do NOT split (their tabs
  // batch via `<x>/project` + ahu_ids_json instead).
  const isAhuReport =
    hasScope(report, 'ahu') && !hasScope(report, 'force-ahu') && !hasScope(report, 'project');
  const isBatched = BATCHED_MULTI_AHU_REPORTS.includes(report.value);

  if (isAhuReport && pickedAhuIds.length > 1 && !isBatched) {
    // :539-548 — N dispatches, each a single-AHU state.
    return pickedAhuIds.map((ahuId) => ({
      ...base(),
      ahu_id: String(ahuId),
      ahu_ids: [ahuId],
      ahu_ids_json: JSON.stringify([ahuId]),
    }));
  }

  const state = base();
  if (isAhuReport && pickedAhuIds.length === 1) {
    state.ahu_id = String(pickedAhuIds[0]); // :551-553
  }
  // ⚠ dev-sync G / CB-2 — VERIFIED BY INSPECTION ONLY, NEVER EXECUTED.
  // main_header.js:582-593. A single-AHU FMI pick carries that AHU's page
  // count so recordPendingReports can force the pending row to 'zip' when the
  // backend will emit more than one drawing. Note the legacy guard is a bare
  // `isFmiAssembly && picked.length === 1` — it does NOT also require
  // isAhuReport, so it is reproduced without that clause. UNTESTABLE on dev3:
  // no `ksdm_master_ahu_report` row for value='fmi_assembly', so
  // report.value can never be 'fmi_assembly' and this stays 0.
  if (report.value === 'fmi_assembly' && pickedAhuIds.length === 1) {
    const row = (input.ahuRows || []).find((a) => a.id === pickedAhuIds[0]);
    state.fmi_page_count = Number(row?.reports?.fmi_assembly_page_count) || 0;
  }
  // Dev-sync I — the Coil Analytics generate snapshots the header's date range
  // onto the dispatch state (main_header.js:699-704 on merged dev). Other
  // reports never carry the keys (the legacy state does retain them after a
  // coil_analytics run, but no backend reads them — not reproduced).
  if (report.value === 'coil_analytics' && input.coilAnalyticsRange) {
    state.start_date = input.coilAnalyticsRange.start;
    state.end_date = input.coilAnalyticsRange.end;
  }
  return [state];
}

/**
 * checkSubmittalPackageIsInProgess (action.js:95-110): a Submittal Package
 * generate is blocked while a pending row with the same report name, user
 * initials and project name is younger than 1 hour.
 */
export function checkSubmittalPackageInProgress(state: GenerateState): boolean {
  if (!SUBMITTAL_GUARDED_REPORTS.has(state.current_report)) return false;
  const rows = readPendingReports();
  const initials = getUserInitials();
  return rows.some(
    (el) =>
      state.current_report_name === el.report &&
      el.user_initials === initials &&
      state.project_name === el.project_name &&
      Date.now() - new Date(el.started_at as string).getTime() < ONE_HOUR_MS
  );
}

/**
 * Reports that tell the user when the server refuses a generate, and take
 * their own optimistic pending row back when it does.
 *
 * AHU Acoustics only, per Artashes: it is the one report the server can refuse
 * for a reason worth reading (no fan both selected and rated — reachable when
 * the board's AHU list has gone stale), and the one the behaviour was asked
 * for. Everywhere else the legacy error path is untouched: a refusal is
 * silent and leaves its row at "generating report...", as it does on `dev`.
 *
 * Widening this is not just a matter of adding values. Several reports strip
 * the AHU from the state inside their own dispatcher, and Submittal Package's
 * 1h duplicate guard reads the pending row — see `dropPendingRowsForDispatch`.
 */
const REJECTION_SURFACED_REPORTS = new Set(['ahu_acoustics']);

/** action.js generateReport (:209-224) for ONE dispatch state. */
function dispatchGenerate(state: GenerateState, ui: GenerateUi): void {
  state.project_id = state.project_id ? state.project_id : '0'; // :211
  state.ahu_id = state.ahu_id ? state.ahu_id : '0'; // :212

  if (checkSubmittalPackageInProgress(state)) {
    ui.onBlocked(); // :213-218
    return;
  }

  ui.onDispatched(state); // :219,223

  // The tab view's POST (the 'generate:report:<name>' fan-out, :114). Reports
  // without an entry render client-side in their 5c–5f tabs — legacy fires no
  // generate POST for them either (er_wheel_selection/ahu_model fetch a
  // collection and render; filter_data/open_projects/order_acknowledgment
  // render their static shells in initialize; timeline fetches its three
  // collections per generate in its pane; project_list registers NO generate
  // listener at all — its once-per-page construction fetch lives in its pane,
  // and the server-side /api/reports/project_list/<user_id> feeder is called
  // by no frontend code). Resolution = the legacy $.ajax
  // success (onTabRendered); rejection = the legacy error path (pane stays
  // empty, timers still revert) — except for the reports in
  // `REJECTION_SURFACED_REPORTS`, which also drop their pending rows and
  // surface the server's reason through `GenerateUi.onRejected`.
  // Set by `recordPendingReports` below, read by the rejection handler inside
  // the dispatch promise (which cannot run before the assignment).
  let dispatchId: string | null = null;

  const dispatch = REPORT_DISPATCH[state.current_report];
  if (dispatch) {
    // The report TYPE's catalog row, read before dispatching (some dispatchers
    // delete keys off the state). Submittal packages are reports too.
    const report = { name: state.current_report_name, value: state.current_report };
    dispatch(state)
      .then(() => {
        // The server accepted the generation request (the file itself arrives
        // later through the notifications poll).
        track('report_generated', report);
        ui.onTabRendered?.(state);
      })
      .catch((error: unknown) => {
        // Every other report keeps the legacy error path exactly: the pane
        // stays empty, the timers still revert, and the optimistic pending row
        // stays put. Only AHU Acoustics takes its row back and says why
        // (`REJECTION_SURFACED_REPORTS`). `fetch` cannot settle before
        // `recordPendingReports` runs on the next statement, so `dispatchId`
        // is always assigned by the time this runs.
        if (!REJECTION_SURFACED_REPORTS.has(state.current_report)) return;
        if (dispatchId) dropPendingRowsForDispatch(dispatchId);
        const message = rejectionMessage(error);
        if (message) ui.onRejected?.(message);
      });
  }

  dispatchId = recordPendingReports(state, ui); // updateReportsData (:112-181)
}

/**
 * Take back exactly the rows `recordPendingReports` wrote for one dispatch,
 * by the id it stamped on them — every format it wrote goes together.
 *
 * Matching on report/project/AHU instead does not work: scope_of_supply,
 * submittal_cover, submittal_package, an AHU-less proposal and the ca_*
 * family all `delete state.ahu_id` synchronously inside their dispatcher, so
 * the row is written with no `ahu_id` at all; and two runs of the same report
 * for the same AHU are indistinguishable, so a refusal would drop a row
 * belonging to an earlier run the server had accepted.
 */
function dropPendingRowsForDispatch(dispatchId: string): void {
  removePendingReportsWhere((row) => row.dispatch_id === dispatchId);
}

let dispatchIdCounter = 0;

/**
 * updateReportsData (action.js:112-181) minus the tab-view fan-out (done
 * above): optimistic pending rows + storage event + the 4.5s/5s timers.
 *
 * Returns the id stamped on the rows it wrote, so a refused dispatch can take
 * back its own (`dropPendingRowsForDispatch`), or null when this report keeps
 * no pending row.
 */
function recordPendingReports(state: GenerateState, ui: GenerateUi): string | null {
  if (NO_PENDING_ROW_REPORTS.has(state.current_report)) return null; // :115-118

  window.setTimeout(() => ui.onSettling(), SETTLE_MS); // timer2 :124-128
  window.setTimeout(() => {
    // timer3 :130-133 — auto-open the header Recent-Reports panel…
    window.dispatchEvent(new CustomEvent(SHOW_REPORTS_LIST_EVENT));
    // …and the unnamed 5s timer :176-180 — back to the empty board.
    ui.onFinished();
  }, FINISH_MS);

  const isMultiAhuZip =
    MULTI_AHU_ZIP_REPORTS.has(state.current_report) &&
    state.ahu_ids &&
    state.ahu_ids.length > 1; // :134-145
  // ⚠ dev-sync G / CB-2 — VERIFIED BY INSPECTION ONLY, NEVER TRUE.
  // action.js:147-153 — a SINGLE-AHU FMI pick still returns a zip when that
  // AHU has more than one drawing page, so its pending row must say 'zip' too
  // or the header reconciler never matches the arriving .zip notification.
  // UNTESTABLE on dev3: no `ksdm_master_ahu_report` row for
  // value='fmi_assembly', so fmi_page_count is always 0.
  const isSingleAhuMultiFanFmi =
    state.current_report === 'fmi_assembly' &&
    state.ahu_ids &&
    state.ahu_ids.length === 1 &&
    Number(state.fmi_page_count || 0) > 1;
  const formats =
    isMultiAhuZip || isSingleAhuMultiFanFmi
      ? ['zip']
      : getAvailableFormatsForReport(state.current_report) || ['']; // :150-154

  dispatchIdCounter += 1;
  const dispatchId = `d${Date.now()}-${dispatchIdCounter}`;
  const rows = readPendingReports();
  formats.forEach((format) => {
    rows.push({
      dispatch_id: dispatchId,
      project_name: state.project_name,
      report: state.current_report_name,
      // The row's identity for every later decision (format lookup, the
      // projectless-match list, the FMI/Proposal zip cases). The display name
      // above is master data and only has to match the notification's own
      // `report` field, which comes from the same master row.
      report_value: state.current_report,
      user_initials: getUserInitials(),
      finished_at: 'generating report...',
      progress: true,
      last_id: Number(localStorage.getItem(LAST_GENERATED_REPORT_KEY)) || 0,
      started_at: new Date(),
      format,
      // dev-sync G / CB-2 (action.js:165) — written on EVERY row, 0 for every
      // non-FMI report; the header's normalizePendingFormat re-reads it when
      // reconciling (pendingReports.ts).
      fmi_page_count: state.fmi_page_count || 0,
      project_id: state.project_id,
      ahu_id: state.ahu_id,
    } satisfies PendingReportRow); // :156-168
  });
  writePendingReportsAndNotify(rows); // :170-182
  return dispatchId;
}

// ---- Per-report dispatch table (the tab views' generateReport POSTs) ----

type DispatchFn = (state: GenerateState) => Promise<void>;

/** Whole-state payload — jQuery serializes the state object as-is (arrays →
 *  `key[]=` pairs), so every field rides along. */
function wholeState(state: GenerateState): Record<string, FormValue> {
  return {
    project_id: state.project_id,
    ahu_id: state.ahu_id,
    selected_ahu_ids: state.selected_ahu_ids,
    current_report: state.current_report,
    reportType: state.reportType,
    generated: state.generated,
    current_report_name: state.current_report_name,
    project_name: state.project_name,
    ahu_ids: state.ahu_ids,
    ahu_ids_json: state.ahu_ids_json,
  };
}

function omit(
  data: Record<string, FormValue>,
  key: string
): Record<string, FormValue> {
  const copy = { ...data };
  delete copy[key];
  return copy;
}

/** Generate POST — the legacy `$.ajax` success only re-renders the tab shell
 *  (surfaced as GenerateUi.onTabRendered by dispatchGenerate); delivery
 *  happens via the notifications poll. A non-2xx throws `PostRejectedError`;
 *  `dispatchGenerate` swallows it like the legacy no-op error path for every
 *  report except those in `REJECTION_SURFACED_REPORTS`. */
async function post(url: string, data: Record<string, FormValue>): Promise<void> {
  await postToCelery(url, data);
}

/** ids fallback shared by the UTD/electrical/apd tabs
 *  (unit_technical_data.js:60-61 et al.). */
function idsOf(state: GenerateState): number[] {
  if (state.ahu_ids && state.ahu_ids.length) return state.ahu_ids;
  return state.ahu_id ? [Number(state.ahu_id)] : [];
}

/** generetionTimeStamp (submittal.package.js / mfs_profile_cut_list.js:183-205)
 *  — 'YYYY.MM.DD_hh:mm:ss'. */
export function legacyTimestamp(now: Date = new Date()): string {
  const p = (n: number) => (n < 10 ? `0${n}` : String(n));
  return (
    `${now.getFullYear()}.${p(now.getMonth() + 1)}.${p(now.getDate())}_` +
    `${p(now.getHours())}:${p(now.getMinutes())}:${p(now.getSeconds())}`
  );
}

/** The order tabs' generetionTimeStamp (sales_order.js:180-203 =
 *  service_order.js:186-209 = shipping_order.js:225-248) — 'YYMMDD_hhmmss':
 *  two-digit year slice, NO dots or colons — a DIFFERENT format from
 *  legacyTimestamp's 'YYYY.MM.DD_hh:mm:ss'. */
export function orderTimestamp(now: Date = new Date()): string {
  const p = (n: number) => (n < 10 ? `0${n}` : String(n));
  return (
    `${String(now.getFullYear()).slice(2)}${p(now.getMonth() + 1)}${p(now.getDate())}_` +
    `${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}`
  );
}

/** Same-tab download click (unit_technical_data.js:205-215 /
 *  integrated_project_ledger.js:78-84) — shared by 5c–5e export flows. */
export function clickDownload(href: string, downloadName: string): void {
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.href = href;
  a.download = downloadName;
  a.click();
  document.body.removeChild(a);
}

/**
 * The Unit-Technical-Data CSV socket→download leg, ported 1:1 from
 * unit_technical_data.js export_csv (:173-220): POST
 * 'technical_data/export/csv/ahu' with the header state (+ generate_id,
 * mfg_id 1) → wait for the socket 'data' message by task_id (:197-204) →
 * <a download> click on msg.data.download_link, named from the
 * project-name index slice (:205-215).
 *
 * NOT wired to any UI, faithfully: the only legacy trigger chain is the
 * header's 'click #export_csv' (main_header.js:62 → :670-684 →
 * 'export_csv:report' → action.js:226-229 →
 * 'export_csv:report:unit_technical_data'), and tmpl/main_header.tmpl
 * renders no #export_csv button (only #generate_report, :45) — the chain is
 * unreachable on the live board. Reaching it would also throw immediately:
 * `self.data[self.current_state].data.generate_id` (:175-176) reads a cache
 * only the fully-commented export_pdf block (:114-154) ever wrote, and the
 * socket handler double-checks the same dead cache (:203). Callers must
 * therefore supply generateId explicitly; the legacy `if
 * (self.data[self.current_state])` guard maps to this function simply not
 * being called without one.
 */
export async function exportUnitTechnicalDataCsv(
  state: GenerateState,
  generateId: string,
  projectName: string,
  ahuName: string
): Promise<void> {
  const { promise } = ajaxPostToCelery('/reports/technical_data/export/csv/ahu', {
    ...wholeState(state),
    project_name: projectName,
    ahu_name: ahuName,
    generate_id: `${generateId}`, // :175-176 string-coerce
    mfg_id: 1, // :183
  });
  const msg = await promise;
  const downloadLink = (msg.data as { download_link?: string } | undefined)?.download_link;
  if (downloadLink == null) return;
  // :208-213 — the download name is the link sliced from the project-name
  // match ('' → indexOf 0 → whole link).
  const prefixed = projectName ? `${projectName}_` : '';
  const index = downloadLink.indexOf(prefixed);
  clickDownload(downloadLink, downloadLink.slice(index));
}

function mfgId(): number {
  return getKsdmConfig()?.mfg?.id ?? 1;
}

/**
 * Fan-curve canvas images (5d): the legacy multi-AHU Fan Selection and
 * Submittal Package flows rasterize fan-curve canvases and POST them as
 * `img[]` — the multi-AHU chain fetches+renders each AHU and captures the
 * tab's canvases (fan_data.js:180-203), submittal draws the project's fans on
 * detached canvases (submittal.package.js:395-417 + getCanvasData :386-392).
 * The REAL provider is fanCurveImages.collectReportCurveImages, registered at
 * module scope by components/FanDataTab.tsx (which the page always loads via
 * GENERATED_TAB_PANES). It owns the multi-AHU per-AHU fetches too — in the
 * legacy those fetches ARE the render/capture source, not separable probes —
 * and its rejection makes the dispatch skip the follow-up POST exactly like
 * the legacy broken promise chain. Unregistered (kit used standalone), the
 * empty default keeps the 5b behavior: no images → the batched fan POST is
 * skipped (:208) and submittal posts an empty img[].
 */
let curveImageProvider:
  | ((state: GenerateState) => Promise<string[]>)
  | null = null;
export function registerCurveImageProvider(
  provider: (state: GenerateState) => Promise<string[]>
): void {
  curveImageProvider = provider;
}
async function collectCurveImages(state: GenerateState): Promise<string[]> {
  if (!curveImageProvider) return [];
  return curveImageProvider(state);
}

/** Backbone model.save with a FormData body (sales_terms.js:52-63 — jQuery
 *  processData:false/contentType:false → multipart). Rejects on failure
 *  (5e fix): the legacy `.save(…).then(render)` runs on the jqXHR's SUCCESS
 *  only, so a failed POST must NOT resolve the dispatch — resolving would
 *  fire onTabRendered and wrongly fill the pane shell. */
async function postMultipart(url: string, fields: Record<string, string | number>): Promise<void> {
  const body = new FormData();
  Object.entries(fields).forEach(([k, v]) => body.append(k, String(v)));
  const resp = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-CSRFToken': getCookie('csrftoken') ?? '',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body,
  });
  if (!resp.ok) {
    // Left as a bare Error: the six CMS document reports that use this helper
    // are not in `REJECTION_SURFACED_REPORTS`, so nothing would read the body.
    throw new Error(`postMultipart ${url} failed: ${resp.status}`);
  }
}

/** The six CMS document reports share one flow: POST multipart {mfg_id: 1} to
 *  /reports/cms_documents/<value>/ (sales_terms/ahu_guide_specs et al. view
 *  generateReport + model/all.js baseUrl). */
function cmsDocumentDispatch(value: string): DispatchFn {
  return async () => {
    await postMultipart(`/reports/cms_documents/${value}/`, { mfg_id: 1 });
  };
}

/** The six 2D drawing reports POST '<value>/ahu' with the whole state
 *  (_2d_drawing.js:56-71). */
function drawingDispatch(value: string): DispatchFn {
  return async (state) => post(`/reports/${value}/ahu`, wholeState(state));
}

/**
 * 5e — the order tabs' generateReport (sales_order.js:85-116,
 * service_order.js:76-108, shipping_order.js:120-151; all three identical):
 * POST '<value>' with the whole state → the dispatch resolves at POST success
 * (the legacy `success: self.render()` shell moment) while the LIVE socket
 * wait continues detached and stashes the full message + the order-format
 * timestamp under the 2-part project key (`self.data[state] = data;
 * … time_stamp = generetionTimeStamp()`, sales_order.js:110-112) — the pane
 * subscribes via reportTabData and renders in-tab, like phx_selection.
 */
function orderDispatch(
  value: 'sales_order' | 'service_order' | 'shipping_order'
): DispatchFn {
  return async (state) => {
    const { task_id: taskId } = await postToCelery(`/reports/${value}`, wholeState(state));
    waitForCeleryTask(taskId)
      .promise.then((msg) => {
        storeGeneratedReport(reportCacheKey(state), {
          message: msg,
          timeStamp: orderTimestamp(),
        });
      })
      .catch(() => undefined);
  };
}

/** One-time PRP structure-fields fetch — the proposal view's initialize-time
 *  collection.fetch (proposal.js:48-54; the view is constructed once per page
 *  by ensureReportView, action.js:197-207). Result unused: it only fills the
 *  commented-out proposal_format loop (proposal.tmpl:14-29). */
let prpProposalFormatFetched = false;
function fetchPrpProposalFormatOnce(): void {
  if (prpProposalFormatFetched) return;
  prpProposalFormatFetched = true;
  void fetchCollection('/api/v1.0/user_settings/prp/format/').catch(() => undefined);
}

/**
 * value → POST(s). URLs are the legacy tab-relative paths resolved against
 * the /reports/ page. Every entry cites its tab view.
 */
export const REPORT_DISPATCH: Record<string, DispatchFn> = {
  // unit_technical_data.js:57-100 — >1 ids batches via /project (backend ZIP);
  // otherwise /ahu with the whole state + resolved ahu_id.
  unit_technical_data: async (state) => {
    const ids = idsOf(state);
    if (ids.length > 1) {
      return post('/reports/technical_data/project', {
        project_id: state.project_id,
        ahu_ids_json: state.ahu_ids_json,
      });
    }
    const ahuId = ids.length === 1 ? String(ids[0]) : state.ahu_id;
    return post('/reports/technical_data/ahu', { ...wholeState(state), ahu_id: ahuId });
  },

  // electrical_calc.js:57-93.
  electrical_calc: async (state) => {
    const ids = idsOf(state);
    if (ids.length > 1) {
      return post('/reports/electrical_calc/project', {
        project_id: state.project_id,
        ahu_ids_json: state.ahu_ids_json,
      });
    }
    const ahuId = ids.length === 1 ? String(ids[0]) : state.ahu_id;
    return post('/reports/electrical_calc/ahu', { ...wholeState(state), ahu_id: ahuId });
  },

  // pressure_drop_calc.js:39-75.
  pressure_drop_calc: async (state) => {
    const ids = idsOf(state);
    if (ids.length > 1) {
      return post('/reports/apd/project', {
        project_id: state.project_id,
        ahu_ids_json: state.ahu_ids_json,
      });
    }
    const ahuId = ids.length === 1 ? String(ids[0]) : state.ahu_id;
    return post(`/reports/apd/ahu/${ahuId}`, { ...wholeState(state), ahu_id: ahuId });
  },

  // fan_data.js:169-268 — the one tab whose generate POST round-trips data.
  // Multi (:175-225): render() runs SYNCHRONOUSLY (:177) and generateReport
  // returns at once (:224) while a background promise chain fetches each AHU
  // sequentially, renders it, captures its canvases (all inside the
  // fanCurveImages provider) and fires ONE batched {ahu_ids_json, img[],
  // date} POST — skipped when no images (:208); ANY per-AHU fetch rejection
  // breaks the chain, batch POST included (:191-222). The dispatch therefore
  // resolves immediately (the sync-render moment) and lets the chain run
  // detached. Single (:227-267): POST {…state, fan_data:true} → the response
  // IS the fan-row JSON (FanSelectionView, report/views.py:922-932); the
  // success callback renders the tab (cache write) and getCurve (:60-85)
  // fires the SECOND fan_selection POST — whole state minus fan_data plus
  // the img[] canvas captures — which creates the celery PDF task.
  fan_data: async (state) => {
    const ids = state.ahu_ids && state.ahu_ids.length > 1 ? state.ahu_ids : null;
    if (ids) {
      void (async () => {
        const images = await collectCurveImages(state);
        if (!images.length) return; // fan_data.js:208
        // The legacy payload has NO date key: `data.date` is unset on this
        // path (:215) and jQuery drops it from the serialized body (verified
        // against the live board's POST, 2026-07-13) — encodeForm would keep
        // an undefined field as `date=`, so the field must be omitted here.
        await post('/reports/fan_selection', {
          ahu_ids_json: JSON.stringify(ids),
          'img[]': images,
        });
      })().catch(() => undefined);
      return undefined; // 'fan_selection_multi' (:224) — before the chain settles
    }
    const rows = await postToCelery<FanDataRow[]>('/reports/fan_selection', {
      ...wholeState(state),
      fan_data: true,
    });
    // renderReport (:41-59): get_si_params parse → html() insert → drawCurves.
    // A parse throw aborts BEFORE the insert (no render, no getCurve); a draw
    // throw aborts AFTER it (tab rendered, getCurve skipped) — both escape
    // the legacy success callback, so neither reaches the second POST.
    const fans = Array.isArray(rows) ? rows : [];
    let entries: FanCanvasEntry[];
    try {
      prepareFanRows(fans); // :45-49
      entries = createFanCanvases(fans);
      storeGeneratedReport(generateStateKey(state), {
        models: fans as Array<Record<string, unknown>>,
        timeStamp: legacyTimestamp(),
      }); // the html() insert — FanDataTab renders from this cache entry
      drawFanDataCurves(entries, fans); // :56
    } catch {
      return undefined;
    }
    // getCurve (:60-85): EVERY canvas is captured (blank ones included) and
    // the POST fires even for a fan-less AHU; fired without await — the
    // legacy success callback returns as soon as $.ajax is dispatched.
    const images = captureFanCanvases(entries, (id) => String(id));
    void post('/reports/fan_selection', {
      ...wholeState(state),
      'img[]': images,
    }).catch(() => undefined);
    return undefined;
  },

  // coil_data.js:49-67.
  coil_data: async (state) => post('/reports/coil_selection', wholeState(state)),

  // gas_heater_data.js:38-55.
  gas_heater_data: async (state) => post('/reports/gas_heater_selection', wholeState(state)),

  // evap_pack_data.js:49-68 — mutates current_report to 'evap_pack' first.
  evap_pack_data: async (state) =>
    post('/reports/evap_pack/ahu', { ...wholeState(state), current_report: 'evap_pack' }),

  // weight_calc.js:102-122.
  weight_calc: async (state) => post(`/reports/weights/ahu/${state.ahu_id}`, wholeState(state)),

  // center_of_gravity.js:47-62.
  center_of_gravity_calc: async (state) =>
    post('/reports/center_of_gravity/ahu', wholeState(state)),

  // sound_power_level.js — the AHU Acoustics report; same shape as Center of
  // Gravity (single AHU -> pdf, 2+ AHUs -> one zip via the backend's
  // sound_power_level.ahu.multi task).
  ahu_acoustics: async (state) =>
    post('/reports/sound_power_level/ahu', wholeState(state)),

  // bill_of_materials.js:68-96 — querystring baked into the PATH; ahu_id
  // dropped from query+payload when '0'.
  bill_of_materials: async (state) => {
    let query = `project_id=${state.project_id}&current_report=${state.current_report}`;
    let payload = wholeState(state);
    if (state.ahu_id && state.ahu_id !== '0') {
      query += `&ahu_id=${state.ahu_id}`;
    } else {
      payload = omit(payload, 'ahu_id');
    }
    return post(`/reports/bill_of_materials/${query}`, payload);
  },

  // mfs_profile_cut_list.js:130-147.
  mfs_profile_cut_list: async (state) =>
    post(`/reports/mfs_profile_cut_list/ahu_id=${state.ahu_id}`, wholeState(state)),

  // equipment_list.js:86-110 — generate_id is the view's (unset) field.
  equipment_list: async (state) =>
    post('/reports/equipment_list', {
      project_id: state.project_id,
      generate_id: undefined,
      ahu_ids_json: state.ahu_ids_json,
    }),

  // equipment_schedule.js:33-52.
  equipment_schedule: async (state) =>
    post('/reports/equipment_schedule', {
      project_id: state.project_id,
      ahu_ids_json: state.ahu_ids_json,
    }),

  // pricing.js:52-69.
  pricing: async (state) => post('/reports/pricing/project', wholeState(state)),

  // usage_statistics.js:51-70 — adds the real mfg id.
  usage_statistics: async (state) =>
    post('/reports/usage_statistics', { ...wholeState(state), mfg_id: mfgId() }),

  // Dev-sync I — tabs/coil_analytics/view/coil_analytics.js:126-145 on merged
  // dev: POST 'coil_analytics' with the whole header state (stamped with
  // start_date/end_date, main_header.js:699-704) + the real mfg id
  // (`data.mfg_id = self.mfg.toJSON().id`, :131). A generate-only shell
  // exactly like usage_statistics: the $.ajax success only render()s the
  // static pane (:136-138), the socket block is fully commented out
  // (:146-161), and delivery is the notifications poll
  // (CoilAnalyticsGenerateDataView → call_gen_coil_analytics, which mirrors
  // usage_statistics; report/urls.py `^coil_analytics`).
  coil_analytics: async (state) =>
    post('/reports/coil_analytics', {
      ...wholeState(state),
      ...(state.start_date != null ? { start_date: state.start_date } : {}),
      ...(state.end_date != null ? { end_date: state.end_date } : {}),
      mfg_id: mfgId(),
    }),

  // scope_of_supply.js:38-58 — `delete data.ahu_id` (:43) mutates the SHARED
  // dispatch state, and the legacy 'generate:report:*' trigger runs
  // synchronously BEFORE the pending-row push (action.js:114 vs :150-161) —
  // so the row's `ahu_id` key is dropped from localStorage too. The delete
  // here is pre-await, so recordPendingReports sees it (5e fix); the payload
  // omit stays because encodeForm would keep a deleted key read via
  // wholeState as `ahu_id=` while jQuery drops it entirely.
  scope_of_supply: async (state) => {
    delete (state as Partial<GenerateState>).ahu_id;
    return post('/reports/scope_of_supply', omit(wholeState(state), 'ahu_id'));
  },

  // submittal_cover.js:38-57 — same `delete data.ahu_id` mutation (:43).
  submittal_cover: async (state) => {
    delete (state as Partial<GenerateState>).ahu_id;
    return post('/reports/submittal_cover', omit(wholeState(state), 'ahu_id'));
  },

  // submittal.package.js:395-403 (preGenerateReport deletes ahu_id, draws the
  // project fan curves — the curve provider) + :434-451 (the POST; the $.ajax
  // SUCCESS render()s the static shell → the dispatch resolution is that
  // moment). 5f: the `delete data.ahu_id` (:397) MUTATES the shared dispatch
  // state synchronously at the trigger, BEFORE the pending-row push
  // (action.js:114 vs :150-161) — so the submittal row's ahu_id key is
  // dropped from localStorage too (the 5e scope_of_supply fix; the delete
  // sits before the first await so recordPendingReports sees it). The socket
  // wait (:452-470) is LIVE (uncommented): its only live effect is
  // `self.data[state] = data` under the 2-PART `current_report + project_id`
  // key (:437 — reportCacheKey), which arms the stale-shell reopen
  // (openGeneratedReport :375-385); the viewPDF call inside it is commented
  // out (:463-467), and generate_id/name/pdf_url (:459-461) feed only dead
  // code — not ported. timeStamp: legacy never stamps this cache entry
  // (unlike the order tabs); the field is required bookkeeping here and
  // nothing reads it (setTimeStamp only ran from the commented renderReport).
  submittal_package: async (state) => {
    delete (state as Partial<GenerateState>).ahu_id;
    const images = await collectCurveImages(state);
    const { task_id: taskId } = await postToCelery('/reports/submittal_package', {
      project_id: state.project_id,
      generated: state.generated,
      current_report: state.current_report,
      'img[]': images,
      date: legacyTimestamp(),
      mfg_id: 1,
      ahu_ids_json: state.ahu_ids_json,
    });
    waitForCeleryTask(taskId)
      .promise.then((msg) => {
        storeGeneratedReport(reportCacheKey(state), {
          message: msg,
          timeStamp: legacyTimestamp(),
        });
      })
      .catch(() => undefined);
  },

  // proposal.js:164-193 — the LIVE generateReport (the big socket/sub-view one
  // at :93-162 is fully commented out): `delete data.ahu_id` when falsy/'0'
  // (:170-172) MUTATES the shared dispatch state before the pending-row push
  // (the trigger at action.js:114 runs synchronously before :150-161), so the
  // proposal row's ahu_id key is dropped from localStorage too — same 5e fix
  // as scope_of_supply. mfg_id is the REAL id (self.mfg.toJSON().id, :175) and
  // is added to a local copy only — it does NOT leak into the pending row.
  // POST 'proposal/export' → DOCX via the notifications poll; the success
  // callback only render()s the static shell (:187-189).
  proposal: async (state) => {
    const dropAhu = !state.ahu_id || state.ahu_id === '0';
    if (dropAhu) delete (state as Partial<GenerateState>).ahu_id;
    // First construction of the proposal view fetches the PRP structure-fields
    // collection (proposal.js:48-54, collection/proposal.structure.fields.js →
    // GET /api/v1.0/user_settings/prp/format/). Its toJSON() only feeds the
    // template's proposal_format loop, which is entirely inside an HTML
    // comment (proposal.tmpl:14-29) — invisible — but the request itself is
    // live legacy behavior, so it fires once here (result unused).
    fetchPrpProposalFormatOnce();
    let payload: Record<string, FormValue> = { ...wholeState(state), mfg_id: mfgId() };
    if (dropAhu) payload = omit(payload, 'ahu_id');
    return post('/reports/proposal/export', payload);
  },

  // ca_activities.js:130-159 / ca_materials.js:130-159 / ca_summary.js:79-108
  // — same URL for both branches; ahu_id dropped when '0'; mfg_id hardcoded 1.
  ca_activities: async (state) => post('/reports/ca_activities/project', caPayload(state)),
  ca_materials: async (state) => post('/reports/ca_materials/project', caPayload(state)),
  ca_summary: async (state) => post('/reports/ca_costing/project', caPayload(state)),

  // sales_order.js:85-116 / service_order.js:76-108 / shipping_order.js:120-151
  // — the order family: POST '<name>' with the whole state; the $.ajax success
  // render()s the tab shell (the dispatch resolution → onTabRendered), and the
  // LIVE (uncommented) Promise.all([fn['create.websocket'], req_task]) socket
  // wait matches the celery result by task_id, stashes it under the 2-part
  // `current_report + project_id` key WITH the order-format timestamp
  // (sales_order.js:103-115) and renders the report IN-TAB — the exact
  // phx_selection choreography, so the same waitForCeleryTask + reportTabData
  // leg is used; OrderReportTabs subscribes. Socket payloads:
  // data.sales_order_data (object) / data.service_order_data (array) /
  // data.shipping_order_data (array) — tasks/reports_tasks/*_project.py:40-41.
  sales_order: orderDispatch('sales_order'),
  service_order: orderDispatch('service_order'),
  shipping_order: orderDispatch('shipping_order'),

  // phx_selection.js:176-216 — POST 'phx_selection' with the whole state;
  // the LIVE socket wait (:196-211, Promise.all([fn['create.websocket'],
  // req_task]) → task_id match) stashes the celery result under the state
  // key for the 5d tab (self.data[state] = data + generetionTimeStamp,
  // :205-207). The dispatch promise resolves at POST success — the legacy
  // `success: self.render()` shell moment (:186-188) — while the socket wait
  // continues in the background and notifies the pane through the
  // reportTabData cache. (The legacy wait is a bare socket.on listener; the
  // kit reuses its emit/backoff re-ask like the 5b ledger port.)
  phx_selection: async (state) => {
    const { task_id: taskId } = await postToCelery('/reports/phx_selection', wholeState(state));
    waitForCeleryTask(taskId)
      .promise.then((msg) => {
        storeGeneratedReport(generateStateKey(state), {
          message: msg,
          timeStamp: legacyTimestamp(),
        });
      })
      .catch(() => undefined);
  },

  // integrated_project_ledger.js:53-87 — the one generate flow that DELIVERS
  // over the socket: POST {mfg_id} → wait by task_id → click-download the ZIP
  // at msg.data.url. 5f: the legacy socket handler strips the tab's
  // .integrated-project-ledger-preloader 'active' BEFORE the url check
  // (:75-77) — surfaced to the pane through a reportTabData cache write at
  // the same moment (IntegratedProjectLedgerTab subscribes). The ledger is
  // NOT reopenable (no open:generated_report listener, :21-29), so the entry
  // never feeds a reopen — REOPENABLE_TABS excludes it.
  integrated_project_ledger: async (state) => {
    const { promise } = ajaxPostToCelery('/reports/csv_integrated_project_ledger/', {
      mfg_id: mfgId(),
    });
    const msg = await promise;
    storeGeneratedReport(generateStateKey(state), {
      message: msg,
      timeStamp: legacyTimestamp(),
    });
    const url = (msg.data as { url?: string } | undefined)?.url;
    if (!url) return; // :77
    const idx = url.lastIndexOf('/');
    clickDownload(url, idx >= 0 ? url.slice(idx + 1) : 'integrated_project_ledger.zip');
  },

  // Terms & Conditions ×3 + Guide Specifications ×3 (sales_terms.js:48-72 +
  // model/all.js baseUrl — multipart {mfg_id: 1}).
  sales_terms: cmsDocumentDispatch('sales_terms'),
  shipping_terms: cmsDocumentDispatch('shipping_terms'),
  warranty_terms: cmsDocumentDispatch('warranty_terms'),
  ahu_guide_specs: cmsDocumentDispatch('ahu_guide_specs'),
  condensing_unit_guide_specs: cmsDocumentDispatch('condensing_unit_guide_specs'),
  packaged_unit_guide_specs: cmsDocumentDispatch('packaged_unit_guide_specs'),

  // 2D drawings ×6 (_2d_drawing.js:56-71; values from report/mvc/app.js:170-176).
  plan: drawingDispatch('plan'),
  plan_and_elevation_ns: drawingDispatch('plan_and_elevation_ns'),
  elevation_near_side: drawingDispatch('elevation_near_side'),
  elevation_far_side: drawingDispatch('elevation_far_side'),
  elevation_intake: drawingDispatch('elevation_intake'),
  elevation_discharge: drawingDispatch('elevation_discharge'),
  // ⚠ dev-sync G / CB-3 — VERIFIED BY INSPECTION ONLY, NEVER EXECUTED.
  // The seventh 2D-drawing alias (fmi_assembly.js + report/mvc/app.js:177):
  // POST /reports/fmi_assembly/ahu with the whole state, exactly like the
  // other six (report/urls.py + views.py add the route; ≥2 ahu_ids → one zip
  // task). UNTESTABLE on dev3: no `ksdm_master_ahu_report` row for
  // value='fmi_assembly' → the report button never renders, so nothing can
  // dispatch here. Without this entry the tab would fire NO POST at all.
  fmi_assembly: drawingDispatch('fmi_assembly'),
};

/** Shared CA payload (ca_activities.js:136-146 et al.). 5f: the legacy
 *  `delete data.ahu_id` when unset/'0' (ca_summary.js:87-91 =
 *  ca_activities.js:138-142 = ca_materials.js:138-142) MUTATES the shared
 *  dispatch state synchronously at the trigger, BEFORE the pending-row push
 *  (action.js:114 vs :150-161) — so the CA row's ahu_id key is dropped from
 *  localStorage too (the 5e scope_of_supply fix; caPayload runs pre-await so
 *  recordPendingReports sees it). `data.mfg_id = 1` (ca_summary.js:95) also
 *  mutates the shared object, but nothing downstream reads it — payload copy
 *  only. */
function caPayload(state: GenerateState): Record<string, FormValue> {
  let payload: Record<string, FormValue> = { ...wholeState(state), mfg_id: 1 };
  if (!state.ahu_id || state.ahu_id === '0') {
    delete (state as Partial<GenerateState>).ahu_id;
    payload = omit(payload, 'ahu_id');
  }
  return payload;
}
